import React, { useEffect } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useAtomValue } from 'jotai';
import { Outlet } from 'react-router-dom';

import {
  useAdobeDataLayer,
  usePurchaseIovation,
} from '@consumer-frontend/nc-api';
import {
  accountIdAtom,
  doesServerHaveIncomeDataAtom,
  iovationBlackboxAtom,
  iovationPurchasedAtom,
} from '@consumer-frontend/nc-components';

import { analytics } from '../../utils/adobe-analytics';

export const AuthenticatedLayout: React.FC = () => {
  const accountId = useAtomValue(accountIdAtom);
  const doesServerHaveIncomeData = useAtomValue(doesServerHaveIncomeDataAtom);
  const { isComplete: isBlackboxComplete } = useAtomValue(iovationBlackboxAtom);
  const {
    adobeDataLayer,
    error: adobeError,
    isLoading,
  } = useAdobeDataLayer(accountId, 'apply');

  const { purchaseIovation } = usePurchaseIovation(iovationBlackboxAtom, iovationPurchasedAtom);

  // Dispatch Adobe data
  useEffect(() => {
    // Wait for data to be loaded.
    if (isLoading) { return; }

    if (adobeError) {
      datadogRum.addError(adobeError);

      return;
    }

    if (adobeDataLayer) {
      analytics.identify(adobeDataLayer.accountId, { ...adobeDataLayer });
      // Note this is not the same `track` function defined in utils/adobe-analytics;
      // Adobe normalizes args passed here and calls it internally.
      analytics.track('adobeDataLayerLoaded', { ...adobeDataLayer });
    }
  }, [
    adobeDataLayer,
    adobeError,
    isLoading,
  ]);

  // When the user is authenticated and the blackbox string is generated,
  // then purchase the Iovation report. Since the report must be purchased before
  // the user can submit their application, we are purchasing the report here
  // to hopefully reduce application submission time.
  useEffect(
    () => {
      if (isBlackboxComplete && doesServerHaveIncomeData) {
        purchaseIovation();
      }
    },
    // Exhausted deps is ignored because we don't want the effect to run when
    // purchaseIovation changes. If this effect runs when purchaseIovation
    // changes, it will start a new purchase request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doesServerHaveIncomeData, isBlackboxComplete],
  );

  return <Outlet />;
};
